// NearbyBuildsModal — Oct 22, 2026
//
// "Show the adjuster": the roofs Bardin built (or has on the build calendar)
// within 2 / 5 / 10 miles of a stop, on a map with the list under it. Opened
// from the Today / My Stops Today rows on the Dispatch Board. Robert's idea —
// a roof specialist at an adjuster meeting turns the phone around and shows
// how much of the neighborhood we've already replaced.
//
// Data: one call to builds_near_point (definer RPC, staff only) at 10 mi; the
// radius and period chips filter in the browser, so switching is instant.
// The subject property is the Bardin-orange pin, built roofs are emerald
// dots, scheduled builds amber. No homeowner names anywhere on this screen:
// it is shown to a third party. Mapbox comes from the CDN global (index.html),
// the same way the Canvas map and PropertyHeroMap use it.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Deal } from "../../../lib/types";
import {
  DEFAULT_RADIUS_MI,
  METERS_PER_MILE,
  PERIOD_OPTIONS,
  RADIUS_OPTIONS_MI,
  circlePolygon,
  fetchNearbyBuilds,
  filterNearby,
  formatMiles,
  nearbyHeadline,
  periodMonths,
  statusLine,
  summarizeNearby,
  type NearbyBuild,
  type PeriodKey,
  type RadiusMi,
} from "../../../lib/nearbyBuilds";

declare const mapboxgl: any;

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const STYLE_URLS = {
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
  streets: "mapbox://styles/mapbox/streets-v12",
} as const;
type Basemap = keyof typeof STYLE_URLS;

const SUBJECT_COLOR = "#f46530"; // Bardin orange
const BUILT_COLOR = "#10b981"; // emerald-500
const SCHEDULED_COLOR = "#f59e0b"; // amber-500

const SRC_RADIUS = "nb-radius";
const SRC_BUILDS = "nb-builds";
const LAYER_RADIUS_FILL = "nb-radius-fill";
const LAYER_RADIUS_LINE = "nb-radius-line";
const LAYER_BUILDS = "nb-builds-pts";

export type NearbyBuildsSubject = Pick<
  Deal,
  "id" | "property_street" | "property_city" | "property_state" | "property_lat" | "property_lon"
>;

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function popupHtml(r: NearbyBuild): string {
  const where = [r.street ?? "(no street)", r.city].filter(Boolean).map(esc).join(", ");
  const tag = r.is_retail
    ? "Retail job"
    : r.carrier
    ? `Claim paid by ${esc(r.carrier)}`
    : "Insurance claim";
  const color = r.status === "built" ? BUILT_COLOR : SCHEDULED_COLOR;
  return (
    `<div style="font-family:system-ui;font-size:12px;line-height:1.35;min-width:160px">` +
    `<div style="font-weight:700;color:#0f172a">${where}</div>` +
    `<div style="color:${color};font-weight:600">${esc(statusLine(r))}</div>` +
    `<div style="color:#475569">${tag} · ${esc(formatMiles(r.distance_m))} away</div>` +
    `</div>`
  );
}

function chipClass(active: boolean): string {
  return (
    "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition " +
    (active
      ? "border-slate-900 bg-slate-900 text-white"
      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")
  );
}

export default function NearbyBuildsModal({
  deal,
  onClose,
}: {
  deal: NearbyBuildsSubject;
  onClose: () => void;
}) {
  const lat = deal.property_lat != null ? Number(deal.property_lat) : null;
  const lon = deal.property_lon != null ? Number(deal.property_lon) : null;
  const hasPoint = lat != null && lon != null && Number.isFinite(lat) && Number.isFinite(lon);

  const [rows, setRows] = useState<NearbyBuild[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [radiusMi, setRadiusMi] = useState<RadiusMi>(DEFAULT_RADIUS_MI);
  const [period, setPeriod] = useState<PeriodKey>("all");
  const [basemap, setBasemap] = useState<Basemap>("satellite");
  const [activeId, setActiveId] = useState<string | null>(null);

  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const applyRef = useRef<() => void>(() => {});
  const fittedRadiusRef = useRef<number | null>(null);
  // Our own "style is ready" flag. Mapbox's isStyleLoaded() stays false while
  // sprites and glyphs are still downloading, which is exactly when the first
  // style.load fires — gating on it skipped the layers on a fresh open.
  const styleReadyRef = useRef(false);

  const visible = useMemo(
    () => (rows ? filterNearby(rows, radiusMi, periodMonths(period)) : []),
    [rows, radiusMi, period],
  );
  const summary = useMemo(() => summarizeNearby(visible), [visible]);
  const headline = nearbyHeadline(summary, radiusMi);
  const subjectLabel = [deal.property_street, deal.property_city].filter(Boolean).join(", ");

  // Escape closes; the page behind stays put while the sheet is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // One read at the widest radius.
  useEffect(() => {
    if (!hasPoint) return;
    let cancelled = false;
    setRows(null);
    setErr(null);
    fetchNearbyBuilds({ lat: lat!, lon: lon!, excludeDealId: deal.id })
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((e: any) => {
        if (!cancelled) setErr(e?.message ?? String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [hasPoint, lat, lon, deal.id]);

  // Map init (once per point). Layers are (re)applied on every style.load so
  // the basemap toggle survives — a style switch wipes sources and layers.
  useEffect(() => {
    if (!hasPoint || !mapDiv.current || mapRef.current) return;
    if (typeof mapboxgl === "undefined") {
      setErr("The map library didn't load — check the connection and reopen.");
      return;
    }
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: STYLE_URLS.satellite,
      center: [lon!, lat!],
      zoom: 12,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");

    // The stop itself: orange pin with a halo, on top of everything.
    const el = document.createElement("div");
    el.style.cssText =
      "width:22px;height:22px;border-radius:50%;background:" +
      SUBJECT_COLOR +
      ";border:3px solid #fff;box-shadow:0 0 0 4px rgba(244,101,48,.35),0 1px 3px rgba(0,0,0,.5);";
    el.title = "This property";
    new mapboxgl.Marker({ element: el }).setLngLat([lon!, lat!]).addTo(map);

    const queryBuilds = (point: any) =>
      map.getLayer(LAYER_BUILDS) ? map.queryRenderedFeatures(point, { layers: [LAYER_BUILDS] }) : [];

    map.on("click", (e: any) => {
      const f = queryBuilds(e.point)[0];
      if (!f) return;
      const id = String(f.properties?.id ?? "");
      const html = String(f.properties?.html ?? "");
      const coords = f.geometry?.coordinates;
      if (!coords) return;
      popupRef.current?.remove();
      popupRef.current = new mapboxgl.Popup({ offset: 10, closeButton: true })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);
      setActiveId(id);
    });
    map.on("mousemove", (e: any) => {
      map.getCanvas().style.cursor = queryBuilds(e.point).length ? "pointer" : "";
    });
    map.on("style.load", () => {
      styleReadyRef.current = true;
      applyRef.current();
    });
    mapRef.current = map;
    // The sheet lays out after the first paint and again when the phone
    // rotates; Mapbox only measures its container on its own at init, so
    // follow the container's size ourselves.
    const container = mapDiv.current;
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(container);
    const raf = requestAnimationFrame(() => map.resize());

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      try {
        popupRef.current?.remove();
        map.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
      popupRef.current = null;
      fittedRadiusRef.current = null;
      styleReadyRef.current = false;
    };
  }, [hasPoint, lat, lon]);

  // (Re)draw the radius ring + build dots. Idempotent: adds the sources and
  // layers when the style lacks them, otherwise just swaps their data.
  useEffect(() => {
    applyRef.current = () => {
      const map = mapRef.current;
      if (!map || !hasPoint || !styleReadyRef.current) return;

      const ring = circlePolygon([lon!, lat!], radiusMi * METERS_PER_MILE);
      if (!map.getSource(SRC_RADIUS)) {
        map.addSource(SRC_RADIUS, { type: "geojson", data: ring });
        map.addLayer({
          id: LAYER_RADIUS_FILL,
          type: "fill",
          source: SRC_RADIUS,
          paint: { "fill-color": SUBJECT_COLOR, "fill-opacity": 0.05 },
        });
        map.addLayer({
          id: LAYER_RADIUS_LINE,
          type: "line",
          source: SRC_RADIUS,
          paint: {
            "line-color": SUBJECT_COLOR,
            "line-width": 1.5,
            "line-dasharray": [3, 2],
            "line-opacity": 0.8,
          },
        });
      } else {
        map.getSource(SRC_RADIUS).setData(ring);
      }

      const geo = {
        type: "FeatureCollection",
        features: visible.map((r) => ({
          type: "Feature",
          properties: { id: r.deal_id, status: r.status, html: popupHtml(r) },
          geometry: { type: "Point", coordinates: [r.lon, r.lat] },
        })),
      };
      if (!map.getSource(SRC_BUILDS)) {
        map.addSource(SRC_BUILDS, { type: "geojson", data: geo });
        map.addLayer({
          id: LAYER_BUILDS,
          type: "circle",
          source: SRC_BUILDS,
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 4, 12, 6.5, 15, 9],
            "circle-color": ["match", ["get", "status"], "scheduled", SCHEDULED_COLOR, BUILT_COLOR],
            "circle-opacity": 0.95,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 2,
          },
        });
      } else {
        map.getSource(SRC_BUILDS).setData(geo);
      }

      // Fit to the ring when the radius changes (not on a period or style flip).
      if (fittedRadiusRef.current !== radiusMi) {
        fittedRadiusRef.current = radiusMi;
        const coords = ring.geometry.coordinates[0];
        const bounds = coords.reduce(
          (b: any, c: [number, number]) => b.extend(c),
          new mapboxgl.LngLatBounds(coords[0], coords[0]),
        );
        map.fitBounds(bounds, { padding: 24, duration: 500, maxZoom: 15 });
      }
    };
    applyRef.current();
  }, [visible, radiusMi, hasPoint, lat, lon]);

  // A row in the list flies the map to that roof and opens its popup.
  const focusRow = (r: NearbyBuild) => {
    setActiveId(r.deal_id);
    const map = mapRef.current;
    if (!map || typeof mapboxgl === "undefined") return;
    popupRef.current?.remove();
    popupRef.current = new mapboxgl.Popup({ offset: 10, closeButton: true })
      .setLngLat([r.lon, r.lat])
      .setHTML(popupHtml(r))
      .addTo(map);
    map.flyTo({ center: [r.lon, r.lat], zoom: Math.max(map.getZoom(), 14.5), duration: 600 });
  };

  const switchBasemap = () => {
    const next: Basemap = basemap === "satellite" ? "streets" : "satellite";
    setBasemap(next);
    popupRef.current?.remove();
    styleReadyRef.current = false; // style.load re-adds the ring and the dots
    mapRef.current?.setStyle(STYLE_URLS[next]);
  };

  const periodLabel = PERIOD_OPTIONS.find((p) => p.key === period)?.label ?? "All time";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-stretch justify-center bg-black/55 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nearby-builds-title"
        className="flex h-full w-full flex-col overflow-hidden bg-white sm:h-[88vh] sm:max-w-3xl sm:rounded-2xl sm:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              🏘 Roofs we've built nearby
            </div>
            <h2 id="nearby-builds-title" className="text-[15px] font-extrabold leading-snug text-slate-900 sm:truncate">
              {!hasPoint
                ? "No coordinates on file"
                : err
                ? "Couldn't load nearby builds"
                : rows
                ? headline
                : "Looking around…"}
            </h2>
            <div className="truncate text-[11px] text-slate-500">
              around {subjectLabel || "this property"} · {periodLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            ✕
          </button>
        </div>

        {!hasPoint ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
            <div className="text-3xl">🗺️</div>
            <div className="text-[13px] font-semibold text-slate-800">No map for this address yet</div>
            <div className="max-w-sm text-[12px] text-slate-500">
              This deal has no coordinates on file, so there is nothing to measure five miles from.
              Re-save the property address on the deal page to geocode it, then open this again.
            </div>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-4 py-2">
              {RADIUS_OPTIONS_MI.map((mi) => (
                <button
                  key={mi}
                  type="button"
                  className={chipClass(mi === radiusMi)}
                  onClick={() => setRadiusMi(mi)}
                >
                  {mi} mi
                </button>
              ))}
              <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />
              {PERIOD_OPTIONS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={chipClass(p.key === period)}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={switchBasemap}
                className="ml-auto rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                title="Switch the basemap"
              >
                {basemap === "satellite" ? "🗺 Map view" : "🛰 Satellite"}
              </button>
            </div>

            {/* Map */}
            <div className="relative min-h-[220px] flex-1">
              <div ref={mapDiv} className="absolute inset-0" />
              <div className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-3 rounded-md bg-white/90 px-2 py-1 text-[10px] font-semibold text-slate-600 shadow">
                <span>
                  <span className="mr-1 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: SUBJECT_COLOR }} />
                  This property
                </span>
                <span>
                  <span className="mr-1 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: BUILT_COLOR }} />
                  Built
                </span>
                <span>
                  <span className="mr-1 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: SCHEDULED_COLOR }} />
                  Scheduled
                </span>
              </div>
              {err ? (
                <div className="absolute inset-x-2 top-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 shadow">
                  Couldn't load nearby builds — {err}
                </div>
              ) : !rows ? (
                <div className="absolute inset-x-2 top-2 rounded-md bg-white/90 px-3 py-1.5 text-center text-[11px] font-medium text-slate-600 shadow">
                  Loading nearby builds…
                </div>
              ) : null}
            </div>

            {/* Summary + list */}
            <div className="max-h-[38%] overflow-y-auto border-t border-slate-200">
              {rows && visible.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-600">
                  <span className="font-semibold text-slate-800">
                    {summary.built} built
                    {summary.scheduled > 0 ? ` · ${summary.scheduled} scheduled` : ""}
                    {summary.retail > 0 ? ` · ${summary.retail} retail` : ""}
                  </span>
                  {summary.carriers.slice(0, 4).map((c) => (
                    <span
                      key={c.name}
                      className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-800"
                    >
                      {c.name} · {c.count}
                    </span>
                  ))}
                  {summary.carriers.length > 4 && (
                    <span className="text-slate-500">+{summary.carriers.length - 4} more carriers</span>
                  )}
                </div>
              )}
              {rows && visible.length === 0 && !err && (
                <div className="px-4 py-6 text-center text-[12px] text-slate-500">
                  No roofs on file within {radiusMi} mi
                  {period === "all" ? "" : ` in the ${periodLabel.toLowerCase()}`}.
                  {radiusMi < 10 ? " Try a wider radius." : ""}
                </div>
              )}
              {visible.map((r) => {
                const active = r.deal_id === activeId;
                return (
                  <button
                    key={r.deal_id}
                    type="button"
                    onClick={() => focusRow(r)}
                    className={
                      "flex w-full items-center gap-3 border-b border-slate-100 px-4 py-2 text-left text-[12px] last:border-b-0 " +
                      (active ? "bg-amber-50" : "hover:bg-slate-50")
                    }
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white"
                      style={{ background: r.status === "built" ? BUILT_COLOR : SCHEDULED_COLOR }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-900">
                        {r.street ?? "(no street)"}
                      </span>
                      <span className="block truncate text-[11px] text-slate-500">
                        {[r.city, statusLine(r)].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    {r.is_retail ? (
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        Retail
                      </span>
                    ) : r.carrier ? (
                      <span className="hidden shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-800 sm:inline">
                        {r.carrier}
                      </span>
                    ) : null}
                    <span className="shrink-0 text-[11px] tabular-nums text-slate-500">
                      {formatMiles(r.distance_m)}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
